import { useEffect, useMemo, useState } from "react";
import { getPublicCalendar, type PublicCalendarEntry } from "@/api/public";
import { buildOccupancy } from "@/lib/calendarOccupancy";
import { MonthStrip } from "@/components/MonthStrip";
import { MonthStripBars } from "@/components/MonthStripBars";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useMediaQuery } from "@/lib/useMediaQuery";

type CalendarStyle = "diagonal" | "bars";

function initialStyle(): CalendarStyle {
  if (typeof window === "undefined") return "diagonal";
  const q = new URLSearchParams(window.location.search).get("style");
  return q === "bars" ? "bars" : "diagonal";
}

const PUBLIC_UNITS = ["EG", "OG", "LZ"];
const PFADI_UNITS = ["EG", "OG", "LZ", "DG"];

const YEARS_AHEAD = 3;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoDate(year: number, month1: number, day: number): string {
  return `${year}-${pad2(month1)}-${pad2(day)}`;
}

function formatDeToday(d: Date): string {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function PublicCalendarPage({ includeDG = false }: { includeDG?: boolean }) {
  useDocumentTitle(includeDG ? "Belegungskalender Pfadfinder – PZRM" : "Belegungskalender – PZRM");

  const today = useMemo(() => new Date(), []);
  const unitNames = includeDG ? PFADI_UNITS : PUBLIC_UNITS;
  const isMobile = useMediaQuery("(max-width: 640px)");
  const cellH = isMobile ? 28 : 22;

  const currentYear = today.getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: YEARS_AHEAD + 1 }, (_, i) => currentYear + i),
    [currentYear],
  );
  const [year, setYear] = useState<number>(currentYear);
  const [style, setStyle] = useState<CalendarStyle>(() => initialStyle());

  const [entries, setEntries] = useState<PublicCalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({ year, month: i }));
  }, [year]);

  const range = useMemo(() => {
    return {
      von: isoDate(year, 1, 1),
      bis: isoDate(year, 12, 31),
    };
  }, [year]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getPublicCalendar(range.von, range.bis)
      .then(data => {
        if (!alive) return;
        const filtered = data.filter(e => unitNames.includes(e.einheit_name));
        setEntries(filtered);
        setLoading(false);
      })
      .catch(err => {
        if (!alive) return;
        setError(err?.message || "Kalender konnte nicht geladen werden.");
        setLoading(false);
      });
    return () => { alive = false; };
  }, [range.von, range.bis, unitNames]);

  const occupancy = useMemo(() => buildOccupancy(entries), [entries]);

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      color: "var(--fg)",
      fontFamily: "var(--font-body)",
      padding: isMobile ? "16px 10px" : "24px 16px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <header style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16,
        }}>
          <div>
            <h1 className="display" style={{
              margin: 0,
              fontSize: 28,
              color: "var(--brand-900)",
            }}>
              Belegungskalender{includeDG ? " Pfadfinder" : ""}
            </h1>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
              Pfadfinderzentrum Raumünzach · Stand: {formatDeToday(today)}
            </div>
          </div>
          <a
            href="/app/anfrage"
            style={{
              display: "inline-block",
              padding: "10px 18px",
              background: "var(--brand-700)",
              color: "var(--fg-inverse)",
              textDecoration: "none",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Anfrage stellen
          </a>
        </header>

        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 12,
        }}>
          <YearSwitcher year={year} options={yearOptions} onChange={setYear} />
          <StyleSwitcher style={style} onChange={setStyle} />
        </div>

        {style === "diagonal" ? <Legend /> : <LegendBars />}

        {loading && (
          <div style={{ padding: 24, color: "var(--fg-muted)" }}>Kalender wird geladen…</div>
        )}
        {error && !loading && (
          <div style={{
            padding: 12,
            border: "1px solid var(--status-cancel)",
            borderRadius: 6,
            color: "var(--status-cancel)",
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}
        {!loading && !error && (
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: isMobile ? 12 : 16,
          }}>
            {months.map(({ year: y, month }) => (
              style === "diagonal" ? (
                <MonthStrip
                  key={`${y}-${month}`}
                  year={y}
                  month={month}
                  unitNames={unitNames}
                  occupancy={occupancy}
                  cellHeight={cellH}
                />
              ) : (
                <MonthStripBars
                  key={`${y}-${month}`}
                  year={y}
                  month={month}
                  unitNames={unitNames}
                  entries={entries}
                  today={today}
                  rowHeight={cellH}
                />
              )
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function YearSwitcher({
  year, options, onChange,
}: {
  year: number;
  options: number[];
  onChange: (y: number) => void;
}) {
  const idx = options.indexOf(year);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < options.length - 1;
  const btn = (enabled: boolean): React.CSSProperties => ({
    padding: "6px 10px",
    background: enabled ? "var(--bg-card)" : "var(--bg-subtle)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: enabled ? "var(--fg)" : "var(--fg-subtle)",
    cursor: enabled ? "pointer" : "not-allowed",
    fontSize: 14,
    fontFamily: "var(--font-body)",
  });
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}>
      <button
        type="button"
        style={btn(canPrev)}
        disabled={!canPrev}
        onClick={() => canPrev && onChange(options[idx - 1])}
        aria-label="Vorheriges Jahr"
      >‹</button>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map(y => {
          const active = y === year;
          return (
            <button
              key={y}
              type="button"
              onClick={() => onChange(y)}
              style={{
                padding: "6px 14px",
                background: active ? "var(--brand-700)" : "var(--bg-card)",
                color: active ? "var(--fg-inverse)" : "var(--fg)",
                border: "1px solid " + (active ? "var(--brand-700)" : "var(--border)"),
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                fontFamily: "var(--font-body)",
              }}
            >
              {y}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        style={btn(canNext)}
        disabled={!canNext}
        onClick={() => canNext && onChange(options[idx + 1])}
        aria-label="Nächstes Jahr"
      >›</button>
    </div>
  );
}

function StyleSwitcher({
  style, onChange,
}: {
  style: CalendarStyle;
  onChange: (s: CalendarStyle) => void;
}) {
  const opt = (value: CalendarStyle, label: string) => {
    const active = style === value;
    return (
      <button
        type="button"
        onClick={() => onChange(value)}
        style={{
          padding: "6px 14px",
          background: active ? "var(--brand-700)" : "var(--bg-card)",
          color: active ? "var(--fg-inverse)" : "var(--fg)",
          border: "1px solid " + (active ? "var(--brand-700)" : "var(--border)"),
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 13,
          fontWeight: active ? 600 : 400,
          fontFamily: "var(--font-body)",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginLeft: "auto",
    }}>
      <span style={{ fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>
        Ansicht
      </span>
      {opt("diagonal", "Diagonal")}
      {opt("bars", "Balken")}
    </div>
  );
}

function LegendBars() {
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 16,
      padding: "10px 14px",
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: 8,
      marginBottom: 16,
      alignItems: "center",
      fontSize: 12,
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 24, height: 10, background: "var(--unit-eg)", opacity: 0.85, borderRadius: 3 }} />
        Belegt (Farbe = Einheit)
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{
          width: 16, height: 14,
          background: "color-mix(in oklab, var(--accent) 8%, transparent)",
          border: "1px solid var(--border)",
        }} />
        Wochenende / Feiertag (BW)
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 2, height: 14, background: "var(--brand-700)" }} />
        Heute
      </span>
    </div>
  );
}

function Legend() {
  const swatch = (bg: string, label: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        display: "inline-block",
        width: 16,
        height: 16,
        background: bg,
        border: "1px solid var(--border)",
        borderRadius: 2,
      }} />
      <span style={{ fontSize: 12, color: "var(--fg)" }}>{label}</span>
    </div>
  );
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 16,
      padding: "10px 14px",
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: 8,
      marginBottom: 16,
    }}>
      {swatch("var(--cal-free)", "frei")}
      {swatch("var(--cal-busy)", "belegt")}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <svg width={16} height={16} viewBox="0 0 16 16">
          <polygon points="0,0 16,0 0,16" fill="var(--cal-busy)" />
          <polygon points="16,0 16,16 0,16" fill="var(--cal-free)" />
        </svg>
        <span style={{ fontSize: 12 }}>Abreise (vormittags belegt)</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <svg width={16} height={16} viewBox="0 0 16 16">
          <polygon points="0,0 16,0 0,16" fill="var(--cal-free)" />
          <polygon points="16,0 16,16 0,16" fill="var(--cal-busy)" />
        </svg>
        <span style={{ fontSize: 12 }}>Anreise (nachmittags belegt)</span>
      </div>
      {swatch("color-mix(in oklab, var(--accent) 8%, transparent)", "Wochenende / Feiertag (BW)")}
    </div>
  );
}
